import { appShell } from './app';

/**
 * What is copied from Bridge is a secret (a password, a note) or a link that holds a key, so it does not stay on the
 * clipboard.
 *
 * - In the apps, the shell puts it there (`bridgeCopy`, apps/apple/Bridge/SecretPasteboard.swift): marked so that clipboard
 *   managers skip it, kept off the person's other devices on iPhone and iPad, and emptied after 90 seconds.
 * - In a browser, the page empties the clipboard itself after `CLEAR_AFTER_MS`, but only if this tab is the one in front
 *   and nothing has been copied or cut on the page since: it never wipes what the person copied elsewhere.
 */
const HANDLER = 'bridgeCopy';
export const CLEAR_AFTER_MS = 60_000;

interface Handler {
  postMessage: (message: unknown) => Promise<unknown>;
}

function nativeHandler(): Handler | null {
  if (appShell() === null) return null;
  const webkit = (window as { webkit?: { messageHandlers?: Record<string, Handler | undefined> } }).webkit;
  return webkit?.messageHandlers?.[HANDLER] ?? null;
}

// ------------------------------------------------------------------ the browser's clipboard

let clearTimer: number | undefined;
/** Something was copied or cut on this page after Bridge's copy, which may have replaced it. */
let touched = false;
let listening = false;
let own = false;

function watchCopies(): void {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  const note = (): void => {
    if (!own) touched = true;
  };
  document.addEventListener('copy', note, true);
  document.addEventListener('cut', note, true);
}

function scheduleClear(): void {
  watchCopies();
  touched = false;
  if (clearTimer !== undefined) window.clearTimeout(clearTimer);
  clearTimer = window.setTimeout(() => {
    clearTimer = undefined;
    if (touched || !document.hasFocus()) return;
    navigator.clipboard.writeText('').catch(() => undefined);
  }, CLEAR_AFTER_MS);
}

/** Copies text, falling back to a hidden textarea where the async API is missing. */
export async function copyText(text: string): Promise<boolean> {
  const native = nativeHandler();
  if (native !== null) {
    try {
      if ((await native.postMessage({ text })) === true) return true;
    } catch {
      // the shell refused or has no such handler: the browser's clipboard below
    }
  }
  try {
    if (typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      scheduleClear();
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    watchCopies();
    own = true;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the only fallback for insecure contexts
    const ok = document.execCommand('copy');
    own = false;
    area.remove();
    // the legacy path cannot empty the clipboard later (no permission to write without a gesture), so there is nothing to schedule
    return ok;
  } catch {
    own = false;
    return false;
  }
}
